import { useCallback, useEffect, useState } from 'react';
import DiscoveryService from '../services/discoveryService';

// What the Map tab's route card reads when it opens a curated walk on a day
// (a scheduled walk keeps only its id); a tour's detail is useTourDetail's.
// Module-level, so reopening a card or remounting the tab doesn't read again;
// after ten minutes the next open asks again, showing the last copy meanwhile.
const TTL_MS = 10 * 60 * 1000;
const cache = new Map();    // key -> { at, value, failed }
const inFlight = new Map(); // key -> Promise
// Bumped by clearRouteDetailCache, so a read begun before a logout can't
// write into the cache after it.
let generation = 0;

/**
 * Read as one viewer: cleared at an auth boundary (invalidateSessionCaches).
 */
export function clearRouteDetailCache() {
  generation += 1;
  cache.clear();
  inFlight.clear();
}

function load(key, read) {
  const hit = cache.get(key);
  if (hit && !hit.failed && Date.now() - hit.at < TTL_MS) return Promise.resolve();
  if (inFlight.has(key)) return inFlight.get(key);
  const gen = generation;
  // A read that comes back empty or throws keeps the last copy (the services
  // answer a network error with { success: false }, not a throw).
  const settle = (value) => {
    if (gen !== generation) return;
    const prev = cache.get(key)?.value ?? null;
    cache.set(key, { at: Date.now(), value: value ?? prev, failed: value == null });
  };
  const p = read()
    .then(settle)
    .catch(err => {
      console.error('Route card read failed:', err);
      settle(null);
    })
    .finally(() => { if (gen === generation) inFlight.delete(key); });
  inFlight.set(key, p);
  return p;
}

function useCached(key, read) {
  const [, setTick] = useState(0);
  const run = useCallback(() => {
    if (!key) return undefined;
    let live = true;
    load(key, read).then(() => { if (live) setTick(n => n + 1); });
    return () => { live = false; };
  // `read` is rebuilt every render; the key names what it reads.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => run(), [run]);
  const hit = key ? cache.get(key) : null;
  const retry = useCallback(() => {
    if (!key) return;
    cache.delete(key);
    run();
  }, [key, run]);
  return { value: hit?.value ?? null, failed: !!hit?.failed && hit?.value == null, retry };
}

/** A curated walk by id (GET /api/discovery/walks/:id), null while it loads or for none. */
export function useCuratedWalk(walkId) {
  const { value, failed, retry } = useCached(walkId ? `walk:${walkId}` : null, () => DiscoveryService.getWalk(walkId));
  return { walk: value, failed, retry };
}
